import { useMemo, useState } from 'react';
import PublicDataTable from '../components/PublicDataTable';
import PublicFilterDropdown from '../components/PublicFilterDropdown';
import FilterPanel, { FilterCheckbox, FilterGroup } from '../components/FilterPanel';
import { DirectoryHeader, DirectoryPage, FilterBy, LastUpdated } from '../components/Directory';
import {
  MARKET_PRICE_LISTINGS,
  MARKET_PRICE_LISTING_STATES,
  MARKET_PRICE_LISTINGS_UPDATED,
} from '../data/overviewData';

// Columns follow the exact Figma column order and labels (481:33123);
// Figma draws a vertical rule after Unit
const COLUMNS = [
  { key: 'species',   title: 'Species' },
  { key: 'type',      title: 'Type' },
  { key: 'unit',      title: 'Unit', divider: true },
  { key: 'market',    title: 'Markets/Abattoirs' },
  { key: 'state',     title: 'State' },
  { key: 'lga',       title: 'LGA' },
  { key: 'frequency', title: 'Market Frequency' },
  {
    key: 'price',
    title: 'Average Current Price (₦)',
    render: (value) => `₦${Number(value).toLocaleString('en-NG')}`,
  },
  { key: 'source', title: 'Data Source' },
];

const SPECIES = [...new Set(MARKET_PRICE_LISTINGS.map((row) => row.species))];

// Price Range panel (Figma 1306:47463): one collapsible head per species,
// each with "Lowest Price" / "Highest Price" checkboxes
const PRICE_OPTIONS = [
  { key: 'lowest',  label: 'Lowest Price' },
  { key: 'highest', label: 'Highest Price' },
];

// Keeps, for every species with a selection, only its cheapest and/or
// dearest listing(s) among the rows already filtered by state. Species with
// nothing ticked drop out while any price selection is active.
function applyPriceRange(rows, priceRange) {
  const picked = Object.entries(priceRange).filter(([, keys]) => keys.length);
  if (!picked.length) return rows;

  const keep = new Set();
  picked.forEach(([species, keys]) => {
    const speciesRows = rows.filter((row) => row.species === species);
    if (!speciesRows.length) return;
    const prices = speciesRows.map((row) => row.price);
    const targets = keys.map((key) => (key === 'lowest' ? Math.min(...prices) : Math.max(...prices)));
    speciesRows.forEach((row) => { if (targets.includes(row.price)) keep.add(row.id); });
  });
  return rows.filter((row) => keep.has(row.id));
}

export default function MarketsPrices() {
  const [query, setQuery] = useState('');
  const [state, setState] = useState(MARKET_PRICE_LISTING_STATES[0]);
  // { Cattle: ['lowest'], Goat: ['lowest', 'highest'], ... }
  const [priceRange, setPriceRange] = useState({});
  const [openSpecies, setOpenSpecies] = useState([SPECIES[0]]);

  const rows = useMemo(() => {
    const byState = state === 'All States'
      ? MARKET_PRICE_LISTINGS
      : MARKET_PRICE_LISTINGS.filter((row) => row.state === state);
    return applyPriceRange(byState, priceRange);
  }, [state, priceRange]);

  const selectedCount = Object.values(priceRange).reduce((n, keys) => n + keys.length, 0);

  function togglePrice(species, key) {
    setPriceRange((current) => {
      const keys = current[species] || [];
      return { ...current, [species]: keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key] };
    });
  }

  function toggleSpecies(species) {
    setOpenSpecies((open) => (open.includes(species) ? open.filter((s) => s !== species) : [...open, species]));
  }

  return (
    <DirectoryPage>
      <DirectoryHeader
        eyebrow="Markets Prices Directory"
        subject="Livestock markets prices"
        query={query}
        onQueryChange={setQuery}
      />

      <FilterBy>
        <PublicFilterDropdown
          value={state}
          options={MARKET_PRICE_LISTING_STATES}
          onChange={setState}
          ariaLabel="Filter by state"
        />
        <FilterPanel label="Price Range" activeCount={selectedCount} onClear={() => setPriceRange({})}>
          {(search) => {
            const visible = SPECIES.filter((species) => species.toLowerCase().includes(search));
            if (!visible.length) return <p className="p-[10px] text-[12px] font-medium text-[#a4a4a4]">No matches</p>;
            return visible.map((species) => (
              <FilterGroup
                key={species}
                label={species}
                isOpen={openSpecies.includes(species)}
                onToggle={() => toggleSpecies(species)}
              >
                {PRICE_OPTIONS.map((option) => (
                  <FilterCheckbox
                    key={option.key}
                    label={option.label}
                    checked={(priceRange[species] || []).includes(option.key)}
                    onChange={() => togglePrice(species, option.key)}
                  />
                ))}
              </FilterGroup>
            ));
          }}
        </FilterPanel>
      </FilterBy>

      <PublicDataTable columns={COLUMNS} data={rows} searchQuery={query} pageSize={10} />

      <LastUpdated date={MARKET_PRICE_LISTINGS_UPDATED} />
    </DirectoryPage>
  );
}
